<script setup lang="ts">
import { computed, ref, useSlots, watch } from 'vue'
import type { ManualOverrideReleasePolicy } from '@/services/inventory'
import type { FieldOverrideState } from '@/utils/fieldOverrideState'
import {
  formatOverrideFieldDisplayValue,
  formatOverrideFieldReleaseLabel,
} from '@/utils/overrideFieldDisplay'
import { formatLockedOverridePolicySummary } from '@/utils/overrideReleasePolicy'

const POLICY_OPTIONS: Array<{ label: string; value: ManualOverrideReleasePolicy }> = [
  { label: 'Until feed value changes', value: 'UNTIL_FEED_VALUE_CHANGES' },
  { label: 'Until manually released', value: 'MANUAL_RELEASE_ONLY' },
  { label: 'Until the vehicle is sold', value: 'UNTIL_SOLD' },
  { label: 'Until a specific date', value: 'UNTIL_DATE' },
]

const PROVENANCE_TOOLTIP =
  "This field can't be overridden until feed mapping provenance is resolved."

const props = defineProps<{
  fieldState: FieldOverrideState
  releasePolicy: ManualOverrideReleasePolicy
  expiresAt: string
  /** Parent signals auto-expand for missing expiry validation. */
  expanded?: boolean
  conflictMessage?: string | null
  suppressActions?: boolean
  dealerTimeZone?: string
}>()

const emit = defineEmits<{
  (e: 'update:releasePolicy', value: ManualOverrideReleasePolicy): void
  (e: 'update:expiresAt', value: string): void
  (e: 'toggle-removal'): void
  (e: 'revert-pending'): void
  (e: 'revert-field'): void
  (e: 'fix-conflict'): void
}>()

const slots = useSlots()
const releaseModalOpen = ref(false)
const policyModalOpen = ref(false)
const detailsModalOpen = ref(false)
const provenanceModalOpen = ref(false)
const draftPolicy = ref<ManualOverrideReleasePolicy>(props.releasePolicy)
const draftExpiresAt = ref(props.expiresAt)
const released = ref(props.fieldState.removalStaged)

const currentValue = computed(() => props.fieldState.activeValue ?? props.fieldState.localValue)
const feedValueNow = computed(() => props.fieldState.valueIfReleasedNow)
const feedValueWhenLocked = computed(() => props.fieldState.mappedValueAtLock)
const source = computed(() =>
  formatSource([
    props.fieldState.currentMappingSourceFeed,
    props.fieldState.currentMappingSourceField,
    props.fieldState.currentMappingSourcePath &&
    props.fieldState.currentMappingSourcePath !== props.fieldState.currentMappingSourceField
      ? props.fieldState.currentMappingSourcePath
      : undefined,
  ]),
)
const sourceLocked = computed(() =>
  formatSource([
    props.fieldState.sourceFeed,
    props.fieldState.sourceField,
    props.fieldState.sourcePath && props.fieldState.sourcePath !== props.fieldState.sourceField
      ? props.fieldState.sourcePath
      : undefined,
  ]),
)
const policy = computed(() => policyLabel(props.releasePolicy))
const expirationDate = computed(() => props.fieldState.expiresAt)
const lockedBy = computed(() => props.fieldState.lockedBy)
const lockedAt = computed(() => props.fieldState.lockedAt)

const hasWrappedInput = computed(() => Boolean(slots.default))
const isLocked = computed(
  () => props.fieldState.hasActiveOverride && !props.fieldState.removalStaged && !released.value,
)
const isReleased = computed(() => released.value || props.fieldState.removalStaged)
const showLockedPattern = computed(() => isLocked.value || hasWrappedInput.value)
const isUntilDateDraft = computed(() => draftPolicy.value === 'UNTIL_DATE')
const showRevert = computed(() => props.fieldState.hasUnsavedLocalChange && !props.suppressActions)
const showActions = computed(() => !props.suppressActions)

const fieldContainerState = computed<'normal' | 'unsaved' | 'pending'>(() => {
  if (props.fieldState.hasUnsavedLocalChange || isReleased.value) {
    return 'unsaved'
  }

  if (props.fieldState.hasPendingDraftAction) return 'pending'

  return 'normal'
})
const fieldContainerLabel = computed(() =>
  fieldContainerState.value === 'unsaved'
    ? 'Unsaved changes'
    : fieldContainerState.value === 'pending'
      ? 'Saved draft pending publish'
      : undefined,
)

const shouldAutoExpand = computed(
  () =>
    Boolean(props.expanded) ||
    (props.releasePolicy === 'UNTIL_DATE' && !String(props.expiresAt || '').trim()),
)

watch(
  () => props.releasePolicy,
  (value) => {
    draftPolicy.value = value
  },
)

watch(
  () => props.expiresAt,
  (value) => {
    draftExpiresAt.value = value
  },
)

watch(
  () => props.fieldState.removalStaged,
  (value) => {
    released.value = value
    if (value) detailsModalOpen.value = false
  },
)

watch(
  shouldAutoExpand,
  (value) => {
    if (value && !isReleased.value) openPolicyModal()
  },
  { immediate: true },
)

function formatFieldValue(value: unknown): string {
  return formatOverrideFieldDisplayValue(props.fieldState.field, value, {
    dealerTimeZone: props.dealerTimeZone,
  })
}

function formatModalDisplayValue(value: unknown): string {
  const formatted = formatFieldValue(value)
  return formatted === 'empty' ? 'Empty' : formatted
}

const lockedManualValue = computed(() => props.fieldState.activeValue ?? currentValue.value)
const releasePreviewStatus = computed(() => props.fieldState.releasePreviewStatus)
const releaseTargetUnavailable = computed(() => releasePreviewStatus.value === 'unavailable')

const modalLockedValueLabel = computed(() => formatModalDisplayValue(lockedManualValue.value))

const compactReleaseTargetLabel = computed(() => {
  if (releasePreviewStatus.value === 'unavailable') return 'latest mapped feed value'
  if (releasePreviewStatus.value === 'empty') return 'empty'
  if (feedValueNow.value === null || feedValueNow.value === undefined) return 'No feed value'
  return formatFieldValue(feedValueNow.value)
})

const modalReleaseTargetLabel = computed(() => {
  if (releasePreviewStatus.value === 'unavailable') return 'Latest mapped feed value'
  if (releasePreviewStatus.value === 'empty') return 'Empty'
  if (feedValueNow.value === null || feedValueNow.value === undefined) return 'Not provided'
  return formatModalDisplayValue(feedValueNow.value)
})

const modalFeedValueWhenLockedLabel = computed(() => {
  if (feedValueWhenLocked.value === null || feedValueWhenLocked.value === undefined) return 'Not provided'
  return formatFieldValue(feedValueWhenLocked.value)
})

const sourceLabel = computed(() => source.value || 'Not available')
const sourceLockedLabel = computed(() => sourceLocked.value || 'Not available')

const pendingReleaseMessage = computed(() => {
  if (releasePreviewStatus.value === 'unavailable') {
    return 'Release pending: Will return to the latest mapped feed value after save and publish.'
  }
  return `Release pending: Will return to ${compactReleaseTargetLabel.value} after save and publish.`
})

const releaseModalFieldLabel = computed(() =>
  formatOverrideFieldReleaseLabel(props.fieldState.field),
)

const lockedPolicySummary = computed(() =>
  formatLockedOverridePolicySummary(props.releasePolicy, props.expiresAt || expirationDate.value),
)

const overrideFieldDisplayLabel = computed(() => {
  const label = formatOverrideFieldReleaseLabel(props.fieldState.field)
  if (label) return label
  return props.fieldState.field
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
})

const policyModalFieldLabel = computed(() => overrideFieldDisplayLabel.value)
const detailsModalFieldLabel = computed(() => overrideFieldDisplayLabel.value)

const policyModalCurrentSummary = computed(() => lockedPolicySummary.value)

function formatSource(parts: Array<string | undefined>): string {
  return parts
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' / ')
}

function policyLabel(value: ManualOverrideReleasePolicy | undefined): string {
  switch (value) {
    case 'MANUAL_RELEASE_ONLY':
      return 'Manual only'
    case 'UNTIL_SOLD':
      return 'Until the vehicle is sold'
    case 'UNTIL_DATE':
      return 'Until a specific date'
    case 'UNTIL_FEED_VALUE_CHANGES':
    default:
      return 'Until feed value changes'
  }
}

function openDetailsModal(): void {
  if (props.fieldState.overrideBlocked) return
  detailsModalOpen.value = true
}

function closeDetailsModal(): void {
  detailsModalOpen.value = false
}

function onPolicyControlKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    openPolicyModal()
  }
}

function openPolicyModal(): void {
  if (props.fieldState.overrideBlocked || isReleased.value) return
  draftPolicy.value = props.releasePolicy
  draftExpiresAt.value = props.expiresAt
  policyModalOpen.value = true
}

function closePolicyModal(): void {
  draftPolicy.value = props.releasePolicy
  draftExpiresAt.value = props.expiresAt
  policyModalOpen.value = false
}

function openReleaseModal(): void {
  if (props.fieldState.overrideBlocked || isReleased.value) return
  releaseModalOpen.value = true
}

function closeReleaseModal(): void {
  releaseModalOpen.value = false
}

function confirmRelease(): void {
  released.value = true
  releaseModalOpen.value = false
  detailsModalOpen.value = false
  emit('toggle-removal')
}

function savePolicy(): void {
  emit('update:releasePolicy', draftPolicy.value)
  emit('update:expiresAt', draftExpiresAt.value)
  policyModalOpen.value = false
}
</script>

<template>
  <div
    class="locked-override-field"
    :class="{
      'locked-override-field--with-input': hasWrappedInput,
      'locked-override-field--unsaved': hasWrappedInput && fieldContainerState === 'unsaved',
      'locked-override-field--pending': hasWrappedInput && fieldContainerState === 'pending',
    }"
    :data-field="fieldState.field"
    :data-state="fieldContainerState"
    :aria-label="fieldContainerLabel"
    data-testid="manual-override-field-controls"
  >
    <div
      v-if="hasWrappedInput"
      class="locked-override-input-wrap"
      :class="{ 'is-locked': showLockedPattern && !isReleased }"
      data-testid="locked-override-input-wrap"
    >
      <slot />
      <v-icon
        v-if="showLockedPattern && !isReleased"
        size="16"
        color="amber-darken-3"
        class="locked-override-input-lock"
        data-testid="locked-override-input-lock"
      >
        mdi-lock-outline
      </v-icon>
    </div>

    <div
      v-if="conflictMessage"
      class="locked-override-conflict d-flex align-center flex-wrap ga-1 mt-1"
      data-testid="manual-override-conflict-row"
    >
      <v-chip size="x-small" color="warning" variant="tonal">Conflict</v-chip>
      <span class="text-caption text-warning">{{ conflictMessage }}</span>
      <v-btn
        size="x-small"
        variant="text"
        density="compact"
        color="warning"
        class="locked-override-action-btn px-1"
        data-testid="manual-override-fix-conflict"
        @click="emit('fix-conflict')"
      >
        Change condition to Used
      </v-btn>
      <v-btn
        v-if="fieldState.hasPendingDraftAction"
        size="x-small"
        variant="text"
        density="compact"
        color="warning"
        class="locked-override-action-btn px-1"
        data-testid="manual-override-discard-conflict"
        @click="emit('revert-pending')"
      >
        Discard pending override
      </v-btn>
    </div>

    <div
      v-if="fieldState.provenanceBlocked"
      class="locked-override-strip d-flex align-center flex-wrap ga-1 mt-1"
      data-testid="locked-override-provenance-blocked"
    >
      <v-tooltip location="top" :text="PROVENANCE_TOOLTIP">
        <template #activator="{ props: tooltipProps }">
          <v-icon v-bind="tooltipProps" size="14" color="warning">mdi-alert-outline</v-icon>
        </template>
      </v-tooltip>
      <span class="text-caption text-medium-emphasis" data-testid="manual-override-inline-text">
        Cannot override · Missing feed mapping
      </span>
      <v-btn
        size="x-small"
        variant="text"
        density="compact"
        color="primary"
        class="locked-override-action-btn px-1"
        data-testid="manual-override-learn-more"
        @click="provenanceModalOpen = true"
      >
        Learn more
      </v-btn>
    </div>

    <div
      v-else-if="isReleased"
      class="locked-override-strip locked-override-strip--released d-flex align-center mt-1"
      data-testid="locked-override-released"
    >
      <span
        class="text-caption text-medium-emphasis locked-override-strip-text"
        data-testid="locked-override-released-message"
      >
        {{ pendingReleaseMessage }}
      </span>
      <div
        v-if="showActions && showRevert"
        class="locked-override-strip-actions locked-override-released-actions"
        data-testid="locked-override-released-actions"
      >
        <button
          type="button"
          class="locked-override-action-link locked-override-action-link--secondary"
          data-testid="manual-override-revert-field"
          @click="emit('revert-field')"
        >
          Revert
        </button>
        <span class="locked-override-action-separator" aria-hidden="true">·</span>
        <button
          type="button"
          class="locked-override-action-link locked-override-action-link--secondary"
          data-testid="manual-override-settings-toggle"
          @click="openDetailsModal"
        >
          Details
        </button>
      </div>
    </div>

    <div
      v-else-if="isLocked"
      class="locked-override-strip locked-override-strip--locked d-flex align-center mt-1"
      data-testid="locked-override-status-strip"
      aria-label="Locked override"
    >
      <button
        v-if="fieldState.showSettings"
        type="button"
        class="locked-override-policy-control"
        data-testid="locked-override-edit-policy"
        title="Edit release policy"
        aria-label="Edit release policy"
        @click="openPolicyModal"
        @keydown="onPolicyControlKeydown"
      >
        <v-icon size="14" color="amber-darken-3" data-testid="manual-override-lock-icon">
          mdi-lock-outline
        </v-icon>
        <span
          class="text-caption text-medium-emphasis locked-override-strip-text"
          data-testid="manual-override-inline-text"
        >
          {{ lockedPolicySummary }}
        </span>
      </button>
      <div
        v-else
        class="locked-override-policy-control locked-override-policy-control--static"
        data-testid="locked-override-policy-summary"
      >
        <v-icon size="14" color="amber-darken-3" data-testid="manual-override-lock-icon">
          mdi-lock-outline
        </v-icon>
        <span
          class="text-caption text-medium-emphasis locked-override-strip-text"
          data-testid="manual-override-inline-text"
        >
          {{ lockedPolicySummary }}
        </span>
      </div>

      <div
        v-if="showActions"
        class="locked-override-strip-actions locked-override-actions-inline"
        data-testid="locked-override-actions-inline"
      >
        <button
          v-if="showRevert"
          type="button"
          class="locked-override-action-link locked-override-action-link--secondary"
          data-testid="manual-override-revert-field"
          @click="emit('revert-field')"
        >
          Revert
        </button>
        <span v-if="showRevert" class="locked-override-action-separator" aria-hidden="true">·</span>
        <button
          type="button"
          class="locked-override-action-link locked-override-action-link--danger"
          data-testid="manual-override-release-action"
          @click="openReleaseModal"
        >
          Release
        </button>
        <span class="locked-override-action-separator" aria-hidden="true">·</span>
        <button
          type="button"
          class="locked-override-action-link locked-override-action-link--secondary"
          data-testid="manual-override-settings-toggle"
          @click="openDetailsModal"
        >
          Details
        </button>
      </div>

      <div
        v-if="showActions"
        class="locked-override-strip-actions locked-override-actions-menu"
        data-testid="locked-override-actions-menu"
      >
        <v-menu location="bottom end">
          <template #activator="{ props: menuProps }">
            <button
              type="button"
              v-bind="menuProps"
              class="locked-override-action-link locked-override-action-link--secondary"
              data-testid="locked-override-actions-menu-trigger"
            >
              Actions
            </button>
          </template>
          <v-list density="compact" data-testid="locked-override-actions-menu-list">
            <v-list-item
              v-if="showRevert"
              title="Revert changes"
              data-testid="locked-override-menu-revert"
              @click="emit('revert-field')"
            />
            <v-list-item
              title="Release override"
              data-testid="locked-override-menu-release"
              @click="openReleaseModal"
            />
            <v-list-item
              title="View details"
              data-testid="locked-override-menu-details"
              @click="openDetailsModal"
            />
          </v-list>
        </v-menu>
      </div>
    </div>

    <div
      v-else-if="fieldState.isOverrideEligible"
      class="locked-override-strip d-flex align-center flex-wrap ga-1 mt-1"
      data-testid="locked-override-eligible-strip"
    >
      <span
        class="text-caption locked-override-eligible-text"
        data-testid="manual-override-inline-text"
      >
        Override eligible
      </span>
    </div>

    <div v-else class="locked-override-strip d-flex align-center flex-wrap ga-1 mt-1">
      <v-icon size="14" color="medium-emphasis" data-testid="manual-override-lock-icon">
        mdi-lock-open-outline
      </v-icon>
      <span class="text-caption text-medium-emphasis locked-override-strip-text" data-testid="manual-override-inline-text">
        {{ fieldState.displaySummaryText }}
      </span>
      <div
        v-if="showRevert"
        class="locked-override-strip-actions locked-override-actions-inline"
        data-testid="locked-override-unsaved-actions"
      >
        <button
          type="button"
          class="locked-override-action-link locked-override-action-link--secondary"
          data-testid="manual-override-revert-field"
          @click="emit('revert-field')"
        >
          Revert
        </button>
      </div>
    </div>

    <v-dialog v-model="detailsModalOpen" max-width="560">
      <v-card data-testid="manual-override-details-modal">
        <v-card-item class="locked-override-details-header">
          <v-card-title class="text-h6">Override details</v-card-title>
        </v-card-item>

        <v-card-text class="locked-override-details-content">
          <div
            class="locked-override-details-context"
            data-testid="locked-override-details-context"
          >
            <div class="locked-override-details-context-row">
              <span class="locked-override-details-context-label">Field</span>
              <strong data-testid="locked-override-details-field">{{ detailsModalFieldLabel }}</strong>
            </div>
          </div>

          <div
            class="locked-override-value-grid mb-3"
            data-testid="manual-override-details"
          >
            <div class="locked-override-value-chip">
              <span>Current value</span>
              <strong data-testid="locked-override-details-current-value">
                {{ formatFieldValue(currentValue) }}
              </strong>
            </div>
            <div class="locked-override-value-chip">
              <span>Feed value now</span>
              <strong data-testid="locked-override-details-feed-now">
                {{ compactReleaseTargetLabel }}
              </strong>
            </div>
            <div class="locked-override-value-chip">
              <span>Feed when locked</span>
              <strong data-testid="locked-override-details-feed-locked">
                {{ modalFeedValueWhenLockedLabel }}
              </strong>
            </div>
          </div>

          <v-divider class="mb-2" />

          <div class="locked-override-meta-list text-caption">
            <div class="locked-override-meta-row">
              <v-icon size="14">mdi-source-branch</v-icon>
              <span>Source</span>
              <strong data-testid="locked-override-details-source">{{ sourceLabel }}</strong>
            </div>
            <div class="locked-override-meta-row">
              <v-icon size="14">mdi-lock-outline</v-icon>
              <span>Source when locked</span>
              <strong data-testid="locked-override-details-source-locked">{{ sourceLockedLabel }}</strong>
            </div>
            <div class="locked-override-meta-row">
              <v-icon size="14">mdi-calendar-sync-outline</v-icon>
              <span>Release policy</span>
              <strong data-testid="locked-override-details-policy">{{ policy }}</strong>
            </div>
            <div v-if="expirationDate" class="locked-override-meta-row">
              <v-icon size="14">mdi-calendar-clock</v-icon>
              <span>Expiration date</span>
              <strong data-testid="locked-override-details-expiration">{{ expirationDate }}</strong>
            </div>
            <div v-if="lockedBy" class="locked-override-meta-row">
              <v-icon size="14">mdi-account-lock-outline</v-icon>
              <span>Locked by</span>
              <strong data-testid="locked-override-details-locked-by">{{ lockedBy }}</strong>
            </div>
            <div v-if="lockedAt" class="locked-override-meta-row">
              <v-icon size="14">mdi-clock-outline</v-icon>
              <span>Locked at</span>
              <strong data-testid="locked-override-details-locked-at">{{ lockedAt }}</strong>
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="locked-override-details-actions">
          <v-spacer />
          <v-btn
            variant="text"
            class="locked-override-action-btn"
            data-testid="manual-override-close-details"
            @click="closeDetailsModal"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="provenanceModalOpen" max-width="480">
      <v-card data-testid="manual-override-provenance-modal">
        <v-card-item>
          <v-card-title class="text-h6">Missing feed mapping</v-card-title>
        </v-card-item>
        <v-card-text>
          <p class="text-body-2 mb-2">{{ PROVENANCE_TOOLTIP }}</p>
          <p
            v-if="fieldState.valueChainText"
            class="text-caption text-medium-emphasis mb-0"
            data-testid="manual-override-provenance-chain"
          >
            {{ fieldState.valueChainText }}
          </p>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            class="locked-override-action-btn"
            data-testid="manual-override-close-provenance"
            @click="provenanceModalOpen = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="policyModalOpen" max-width="520">
      <v-card data-testid="manual-override-policy-modal">
        <v-card-item class="locked-override-policy-header">
          <v-card-title class="text-h6">Edit release policy</v-card-title>
          <v-card-subtitle class="text-body-2">
            Choose when this locked override should stop applying.
          </v-card-subtitle>
        </v-card-item>

        <v-card-text class="locked-override-policy-content">
          <div
            class="locked-override-policy-context"
            data-testid="locked-override-policy-context"
          >
            <div class="locked-override-policy-context-row">
              <span class="locked-override-policy-context-label">Field</span>
              <strong data-testid="locked-override-policy-field">{{ policyModalFieldLabel }}</strong>
            </div>
            <div class="locked-override-policy-context-row">
              <span class="locked-override-policy-context-label">Current policy</span>
              <strong data-testid="locked-override-policy-current">{{ policyModalCurrentSummary }}</strong>
            </div>
          </div>

          <v-radio-group
            v-model="draftPolicy"
            density="compact"
            hide-details
            data-testid="locked-override-policy-radios"
          >
            <v-radio
              v-for="option in POLICY_OPTIONS"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </v-radio-group>
          <v-text-field
            v-if="isUntilDateDraft"
            v-model="draftExpiresAt"
            type="datetime-local"
            label="Expires"
            variant="outlined"
            density="compact"
            class="mt-2"
            data-testid="locked-override-policy-date"
          />
        </v-card-text>

        <v-divider />

        <v-card-actions class="locked-override-policy-actions">
          <v-spacer />
          <v-btn
            variant="text"
            class="locked-override-action-btn"
            data-testid="manual-override-cancel-policy"
            @click="closePolicyModal"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            class="locked-override-action-btn"
            data-testid="locked-override-save-policy"
            @click="savePolicy"
          >
            Save policy
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="releaseModalOpen" max-width="520">
      <v-card data-testid="manual-override-release-confirmation">
        <v-card-item class="locked-override-release-header">
          <v-card-title class="text-h6">Release locked override?</v-card-title>
          <v-card-subtitle class="text-body-2" data-testid="locked-override-release-subtitle">
            <template v-if="releaseModalFieldLabel">
              <strong data-testid="locked-override-release-field-label">{{
                releaseModalFieldLabel
              }}</strong>
              will return to the current feed value after save and publish.
            </template>
            <template v-else>
              This field will return to the current feed value after save and publish.
            </template>
          </v-card-subtitle>
        </v-card-item>

        <v-card-text class="locked-override-release-content">
          <div
            class="locked-override-release-comparison"
            data-testid="locked-override-release-comparison"
          >
            <div class="locked-override-release-comparison-col">
              <span class="locked-override-release-comparison-label">Current locked value</span>
              <strong
                class="locked-override-release-comparison-value"
                data-testid="locked-override-release-current"
              >
                {{ modalLockedValueLabel }}
              </strong>
            </div>
            <v-icon class="locked-override-release-comparison-arrow" size="20">
              mdi-arrow-right
            </v-icon>
            <div class="locked-override-release-comparison-col">
              <span class="locked-override-release-comparison-label">Will return to</span>
              <strong
                class="locked-override-release-comparison-value"
                data-testid="locked-override-release-target"
              >
                {{ modalReleaseTargetLabel }}
              </strong>
            </div>
          </div>

          <p
            v-if="releaseTargetUnavailable"
            class="locked-override-release-unavailable-note text-caption text-medium-emphasis"
            data-testid="locked-override-release-unavailable-note"
          >
            We could not preview the exact value.
          </p>
        </v-card-text>

        <v-divider />

        <v-card-actions class="locked-override-release-actions">
          <v-spacer />
          <v-btn
            variant="text"
            class="locked-override-action-btn"
            data-testid="manual-override-cancel-release"
            @click="closeReleaseModal"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            size="default"
            class="locked-override-action-btn"
            data-testid="manual-override-confirm-release"
            @click="confirmRelease"
          >
            Release override
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.locked-override-field {
  width: 100%;
}

.locked-override-field--with-input {
  border: 1px solid transparent;
  border-radius: 10px;
  transition:
    background-color 160ms ease,
    border-color 160ms ease;
}

.locked-override-field--with-input.locked-override-field--unsaved,
.locked-override-field--with-input.locked-override-field--pending {
  background: transparent;
  border-color: transparent;
}

.locked-override-input-wrap {
  position: relative;
  border-radius: 8px;
}

.locked-override-input-wrap.is-locked {
  box-shadow: 0 0 0 1px rgba(253, 186, 116, 0.8);
}

.locked-override-input-lock {
  position: absolute;
  top: 50%;
  right: 10px;
  z-index: 2;
  transform: translateY(-50%);
  pointer-events: none;
}

.locked-override-strip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-height: 24px;
  gap: 4px 8px;
  width: 100%;
}

.locked-override-strip--locked,
.locked-override-strip--released {
  flex-wrap: wrap;
}

.locked-override-policy-control {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  text-align: left;
  border-radius: 4px;
}

.locked-override-policy-control--static {
  cursor: default;
}

.locked-override-policy-control:hover .locked-override-strip-text,
.locked-override-policy-control:focus-visible .locked-override-strip-text {
  text-decoration: underline;
  color: rgba(var(--v-theme-on-surface), 0.82);
}

.locked-override-policy-control:focus-visible {
  outline: 2px solid rgba(var(--v-theme-primary), 0.45);
  outline-offset: 2px;
}

.locked-override-eligible-text {
  color: rgba(var(--v-theme-success), 0.55);
  font-size: 0.6875rem;
}

.locked-override-strip-text {
  flex: 1 1 auto;
  min-width: 0;
}

.locked-override-strip-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
}

.locked-override-actions-inline {
  flex-wrap: wrap;
  gap: 4px;
}

.locked-override-released-actions {
  flex-wrap: wrap;
  gap: 4px;
}

.locked-override-actions-menu {
  display: none;
}

.locked-override-action-link {
  border: 0;
  background: transparent;
  padding: 0;
  font-size: 0.75rem;
  line-height: 1.25;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
}

.locked-override-action-link--danger {
  color: rgb(var(--v-theme-error));
}

.locked-override-action-link--danger:hover,
.locked-override-action-link--danger:focus-visible {
  color: rgb(var(--v-theme-error));
  text-decoration: underline;
}

.locked-override-action-link--secondary {
  color: rgba(var(--v-theme-on-surface), 0.62);
}

.locked-override-action-link--secondary:hover,
.locked-override-action-link--secondary:focus-visible {
  color: rgba(var(--v-theme-on-surface), 0.82);
  text-decoration: underline;
}

.locked-override-action-separator {
  color: rgba(var(--v-theme-on-surface), 0.38);
  font-size: 0.75rem;
  line-height: 1;
  user-select: none;
}

.locked-override-action-btn {
  letter-spacing: normal;
  text-transform: none;
}

.locked-override-value-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.locked-override-value-chip {
  display: grid;
  gap: 2px;
  min-width: 0;
  padding: 8px 10px;
  background: rgba(251, 191, 36, 0.12);
  border: 1px solid rgba(217, 119, 6, 0.22);
  border-radius: 8px;
}

.locked-override-value-chip span,
.locked-override-meta-row span {
  color: rgba(var(--v-theme-on-surface), 0.66);
}

.locked-override-value-chip strong,
.locked-override-meta-row strong {
  overflow-wrap: anywhere;
  font-weight: 600;
}

.locked-override-meta-list {
  display: grid;
  gap: 6px;
}

.locked-override-meta-row {
  display: grid;
  grid-template-columns: 18px minmax(120px, max-content) 1fr;
  gap: 8px;
  align-items: center;
}

.locked-override-pending-release {
  min-width: 0;
}

.locked-override-release-header {
  padding-bottom: 0;
}

.locked-override-release-content {
  padding-top: 12px;
}

.locked-override-release-comparison {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding: 16px;
  border-radius: 10px;
  background: rgba(251, 191, 36, 0.08);
  border: 1px solid rgba(217, 119, 6, 0.16);
}

.locked-override-release-comparison-col {
  display: grid;
  gap: 6px;
  min-width: 0;
  text-align: center;
}

.locked-override-release-comparison-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.62);
}

.locked-override-release-comparison-value {
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.locked-override-release-comparison-arrow {
  color: rgba(var(--v-theme-on-surface), 0.45);
}

.locked-override-release-unavailable-note {
  margin: 10px 0 0;
}

.locked-override-release-actions,
.locked-override-policy-actions {
  padding: 12px 16px 16px;
}

.locked-override-policy-header {
  padding-bottom: 0;
}

.locked-override-policy-content {
  padding-top: 12px;
}

.locked-override-policy-context {
  display: grid;
  gap: 8px;
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
}

.locked-override-policy-context-row {
  display: grid;
  grid-template-columns: minmax(110px, max-content) 1fr;
  gap: 10px;
  align-items: baseline;
}

.locked-override-policy-context-label {
  font-size: 0.8125rem;
  color: rgba(var(--v-theme-on-surface), 0.62);
}

.locked-override-policy-context-row strong {
  font-size: 0.875rem;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.locked-override-details-header {
  padding-bottom: 0;
}

.locked-override-details-content {
  padding-top: 12px;
}

.locked-override-details-context {
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
}

.locked-override-details-context-row {
  display: grid;
  grid-template-columns: minmax(110px, max-content) 1fr;
  gap: 10px;
  align-items: baseline;
}

.locked-override-details-context-label {
  font-size: 0.8125rem;
  color: rgba(var(--v-theme-on-surface), 0.62);
}

.locked-override-details-context-row strong {
  font-size: 0.875rem;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.locked-override-details-actions {
  padding: 12px 16px 16px;
}

@media (max-width: 700px) {
  .locked-override-actions-inline {
    display: none;
  }

  .locked-override-actions-menu {
    display: flex;
  }

  .locked-override-value-grid {
    grid-template-columns: 1fr;
  }

  .locked-override-meta-row {
    grid-template-columns: 18px 1fr;
  }

  .locked-override-meta-row strong {
    grid-column: 2;
  }

  .locked-override-release-comparison {
    grid-template-columns: 1fr;
    justify-items: center;
  }

  .locked-override-release-comparison-arrow {
    transform: rotate(90deg);
  }
}
</style>
